<svelte:options customElement="hb-uploader" />

<script lang="ts">
  import pkg from "../../../package.json";
  import { addComponent, LanguageTranslator } from "wc-js-utils";
  import type { Events as DialogEvents } from "../dialog/types/webcomponent.type";

  import type { Component } from "./types/webcomponent.type";

  /** Merge plain header map into a `Headers` instance (fetch). */
  function toHeaders(headers: Record<string, string> | undefined): Headers {
    const h = new Headers();
    if (!headers) return h;
    for (const [key, value] of Object.entries(headers)) {
      if (value != null && value !== "") h.set(key, value);
    }
    return h;
  }

  /** Build a `Blob` body and whether upload byte progress can be tracked via a stream. */
  function resolveUploadBody(
    data: unknown,
    headers: Headers
  ): { blob: Blob; trackProgress: boolean } {
    if (data instanceof FormData) {
      return { blob: new Blob(), trackProgress: false };
    }
    if (typeof data === "string") {
      return { blob: new Blob([data]), trackProgress: true };
    }
    if (data instanceof Blob) {
      return { blob: data, trackProgress: true };
    }
    if (data instanceof ArrayBuffer) {
      return { blob: new Blob([data]), trackProgress: true };
    }
    if (ArrayBuffer.isView(data)) {
      const view = data as ArrayBufferView;
      const copy = new Uint8Array(view.byteLength);
      copy.set(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));
      return { blob: new Blob([copy]), trackProgress: true };
    }
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    return {
      blob: new Blob([JSON.stringify(data)]),
      trackProgress: true,
    };
  }

  /** ReadableStream over a blob that reports cumulative upload progress (fetch body). */
  function blobStreamWithProgress(
    blob: Blob,
    onProgress: (loaded: number, total: number) => void
  ): ReadableStream<Uint8Array> {
    const total = blob.size;
    const reader = blob.stream().getReader();
    let loaded = 0;
    return new ReadableStream({
      async pull(controller) {
        const { done, value } = await reader.read();
        if (done) {
          controller.close();
          return;
        }
        loaded += value.byteLength;
        onProgress(loaded, total);
        controller.enqueue(value);
      },
      cancel() {
        reader.cancel();
      },
    });
  }

  function dispatch(name: string, detail: any) {
    $host().dispatchEvent(new CustomEvent(name, { detail }));
  }

  addComponent({ repoName: "@htmlbricks/hb-dialog", version: pkg.version });

  let {
    id = "",
    // style = "",
    upload_id = undefined,

    fetch_data = undefined,
  }: Component = $props();

  let total: number = $state(0);
  let loaded: number = $state(0);
  let completed: boolean = $state(false);
  let errorMessage: string | null = $state(null);
  $effect(() => {
    if (!upload_id) upload_id = id + "_dialog";
    // if (style) {
    //     dialogStyleSetup?.vars
    //   );
    // }
    if (!completed) completed = false;

    try {
      if (fetch_data && typeof fetch_data === "string")
        fetch_data = JSON.parse(fetch_data);
    } catch (err) {
      console.error(err);
    }
  });

  async function onModalOpened() {
    function onRequestError(err: Error) {
      console.error(err);
      errorMessage = err?.message || "upload error";

      dispatch("uploadError", { completed, id: upload_id, error: err });
    }
    loaded = 0;
    try {
      if (fetch_data) {
        const method = (fetch_data.method || "POST").toUpperCase();
        const headers = toHeaders(fetch_data.headers);
        const { blob, trackProgress } = resolveUploadBody(
          fetch_data.data,
          headers
        );

        let body: BodyInit;
        if (fetch_data.data instanceof FormData) {
          body = fetch_data.data;
        } else if (trackProgress && blob.size > 0) {
          body = blobStreamWithProgress(blob, (l, t) => {
            total = t;
            loaded = l;
          });
        } else {
          body = blob;
          if (trackProgress && blob.size === 0) {
            total = 0;
            loaded = 0;
          }
        }

        const response = await fetch(fetch_data.url, {
          method,
          headers,
          body,
        });

        if (!response.ok) {
          const text = await response.text().catch(() => "");
          throw new Error(
            text || response.statusText || `Request failed (${response.status})`
          );
        }

        // FormData (and zero-byte bodies) do not get stream progress; show completion.
        if (fetch_data.data instanceof FormData || (trackProgress && blob.size === 0)) {
          total = Math.max(total, 1);
          loaded = total;
        }

        return dispatch("uploadComplete", { completed, id: upload_id });
      } else {
        errorMessage = "No fetch data provided";
        return onRequestError(new Error(errorMessage));
      }
    } catch (error: unknown) {
      console.error("upload error ", error);

      return onRequestError(
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  function dialogShowEvent(d: DialogEvents["modalShow"]) {
    errorMessage = null;

    if (d.show) {
      if (fetch_data?.url) onModalOpened().catch(console.error);
    }
    dispatch("modalShow", d);
  }
</script>

<hb-dialog
  id={upload_id}
  show={fetch_data?.url || (loaded && total) ? "yes" : "no"}
  onmodalShow={(d: { detail: DialogEvents["modalShow"] }) =>
    dialogShowEvent(d.detail)}
>
  <span slot="title">
    <slot name="title">Uploading</slot>
  </span>
  <div slot="body-content">
    {#if !total && !errorMessage}
      <progress
        class="progress is-primary"
        max="100"
        aria-busy="true"
        title="Loading"
      ></progress>
    {:else if !errorMessage}
      <progress
        class="progress is-primary"
        value={Math.round((loaded / total) * 100)}
        max="100"
        aria-valuenow={Math.round((loaded / total) * 100)}
        aria-valuemin="0"
        aria-valuemax="100"
        title="{Math.round((loaded / total) * 100)}%"
      ></progress>
      <span class="hb-uploader-percent"
        >{Math.round((loaded / total) * 100)}%</span
      >
    {:else if errorMessage}
      {errorMessage}
    {/if}
  </div>
  <span slot="modal-footer"></span>
</hb-dialog>

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
